import { CaretDownIcon, CaretUpIcon, HandbagIcon } from "@phosphor-icons/react";
import { formatPrice } from "apps/visits/core/utils/format-price";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";
import { useCartStore } from "../../store/use-cart-store";
import { ProductsList } from "./products-list";

type SideCartProps = {
  onNextStep?: () => void;
  // When set, the CTA becomes a `type="submit"` button associated with the form of this id via the
  // native `form` attribute. This submits the form even though the mobile sheet is portaled to
  // document.body (the button is outside the form's DOM subtree). Mutually exclusive with onNextStep.
  submitFormId?: string;
  buttonLabel?: string;
  ctaDisabled?: boolean;
  ctaLoading?: boolean;
  // Read-only mode (e.g. order summary) hides the per-item remove control.
  readOnly?: boolean;
  // Rendered below the CTA, still inside the cart card — used on summary for the confirm error.
  appendedContent?: ReactNode;
  // Rendered below the whole cart card (outside it), matching VD's layout — used on summary for payment methods.
  belowCart?: ReactNode;
};

// Below lg the cart docks to the bottom of the viewport as a collapsible sheet (VD SideCart UX);
// from lg up it becomes a sticky sidebar column inside SidebarLayout.
const MOBILE_BREAKPOINT = 1024;

export const SideCart = ({
  onNextStep,
  submitFormId,
  buttonLabel,
  ctaDisabled = false,
  ctaLoading = false,
  readOnly = false,
  appendedContent,
  belowCart,
}: SideCartProps) => {
  const { products, getTotalPrice, removeProduct } = useCartStore();
  const { t } = useTranslation();
  const totalPrice = getTotalPrice();
  const hasProducts = products.length > 0;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT,
  );

  // Products already in the cart when the SideCart mounts must not animate; only newly added ones do.
  const settledKeys = useRef<Set<string>>(new Set(products.map((p) => p.uuid)));
  useEffect(() => {
    const currentKeys = new Set(products.map((p) => p.uuid));
    settledKeys.current.forEach((key) => {
      if (!currentKeys.has(key)) settledKeys.current.delete(key);
    });
  }, [products]);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const ctaLabel = buttonLabel ?? t("GROUP_ACTIONS_ORDERING.GENERAL.GO_NEXT");
  // The docked bottom sheet only makes sense once there's something in the cart; an empty cart
  // stays in the desktop sidebar column and is hidden on mobile (matches VD).
  const mobileSheet = isMobile && hasProducts;

  const content = (
    <div
      data-testid="side-cart"
      className={cn({
        "max-lg:hidden": !hasProducts,
        "fixed right-0 bottom-0 left-0 z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] transition-all duration-300":
          mobileSheet,
        "max-lg:h-auto": mobileSheet && !isExpanded,
        "max-lg:h-[85vh]": mobileSheet && isExpanded,
      })}
    >
      <div
        className={cn([
          "flex flex-col gap-4 lg:sticky lg:top-32",
          mobileSheet
            ? `h-full gap-0 rounded-t-2xl bg-white ${isExpanded ? "px-4 pt-7 pb-4" : "p-4"}`
            : "-mt-4 rounded-2xl bg-gradient-to-t from-white via-white to-transparent p-6 shadow-[1px_10px_8px_0px_#00000008]",
        ])}
      >
        <button
          type="button"
          className={cn("flex w-full select-none items-center gap-2 text-left", {
            "relative cursor-pointer justify-between": mobileSheet,
          })}
          onClick={() => mobileSheet && setIsExpanded(!isExpanded)}
        >
          {mobileSheet && (
            <div
              className={cn(
                "absolute left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-[0px_-3px_3px_#0000000a]",
                isExpanded ? "-top-[42px]" : "-top-[30px]",
              )}
            >
              {isExpanded ? <CaretDownIcon size={16} /> : <CaretUpIcon size={16} />}
            </div>
          )}
          <div className="flex w-full items-center gap-2">
            <HandbagIcon size={28} />
            <span className="font-semibold text-black">{t("GROUP_ACTIONS_ORDERING.GENERAL.YOUR_CART")}</span>
          </div>
          {mobileSheet && <span className="shrink-0 font-bold text-lg">{formatPrice(totalPrice)}</span>}
        </button>

        {hasProducts ? (
          <>
            <div
              className={cn("flex-1", {
                hidden: mobileSheet && !isExpanded,
                "overflow-y-auto": mobileSheet && isExpanded,
              })}
            >
              <ProductsList
                products={products}
                onRemoveProduct={readOnly ? undefined : removeProduct}
                isNew={(product) => "uuid" in product && !settledKeys.current.has(product.uuid)}
              />
              <div className="mt-4 flex items-center justify-between border-grey-200 border-t pt-4 text-base">
                <span className="font-semibold">{t("GROUP_ACTIONS_ORDERING.CART.TO_PAY")}</span>
                <span className="font-bold text-3xl">{formatPrice(totalPrice)}</span>
              </div>
            </div>

            <div className="pt-2">
              <Button
                variant="primary"
                size="xl"
                className="w-full"
                {...(submitFormId ? { type: "submit" as const, form: submitFormId } : { onClick: onNextStep })}
                loading={ctaLoading}
                disabled={ctaDisabled}
                data-testid="side-cart-cta"
              >
                {ctaLabel}
              </Button>
              {appendedContent}
            </div>
          </>
        ) : (
          <div className="pointer-events-none flex select-none flex-col items-center justify-center rounded-3xl py-8">
            <p className="text-center opacity-20">{t("GROUP_ACTIONS_ORDERING.CART.EMPTY")}</p>
          </div>
        )}
      </div>
      {hasProducts && belowCart}
    </div>
  );

  if (mobileSheet) {
    return createPortal(content, document.body);
  }

  return content;
};
